<template>
  <div class="news-stat-v3">
    <!-- Header Section -->
    <div class="stat-section-header">
      <div class="header-title-group">
        <div class="header-icon-badge">
          <i class="el-icon-reading"></i>
        </div>
        <div>
          <h2 class="header-title">Thống Kê Tin Tức & Bản Tin Học Đường</h2>
          <p class="header-subtitle">Tin đang hiển thị theo ngày tạo, phân bổ cấp trường và cấp lớp</p>
        </div>
      </div>

      <div v-if="overview" class="header-badge-group">
        <span class="live-indicator">
          <span class="pulse-dot"></span> Tin đang hiển thị, tạo trong tháng: {{ overview.monthCount }} tin
        </span>
      </div>
    </div>

    <el-alert v-if="overviewError" :title="overviewError" type="error" show-icon :closable="false" class="news-api-alert" />

    <!-- 1. Cụm chỉ số: Tổng số tin (Ngày, Tuần, Tháng), Trường đăng, Lớp đăng -->
    <div v-if="overview" class="news-kpi-grid">
      <!-- Tin trong ngày -->
      <div class="news-kpi-card border-indigo">
        <div class="kpi-icon-wrap bg-indigo-soft text-indigo">
          <i class="el-icon-date"></i>
        </div>
        <div class="kpi-details">
          <span class="kpi-name">Tin tạo hôm nay</span>
          <div class="kpi-number text-indigo">{{ overview.todayCount }} <span class="kpi-unit">tin</span></div>
          <div class="kpi-hint">{{ deltaLabel }}</div>
        </div>
      </div>

      <!-- Tin trong tuần -->
      <div class="news-kpi-card border-blue">
        <div class="kpi-icon-wrap bg-blue-soft text-blue">
          <i class="el-icon-notebook-1"></i>
        </div>
        <div class="kpi-details">
          <span class="kpi-name">Tin tạo tuần này</span>
          <div class="kpi-number text-blue">{{ overview.weekCount }} <span class="kpi-unit">tin</span></div>
          <div class="kpi-hint">{{ weekLabel }}</div>
        </div>
      </div>

      <!-- Tin trong tháng -->
      <div class="news-kpi-card border-teal">
        <div class="kpi-icon-wrap bg-teal-soft text-teal">
          <i class="el-icon-files"></i>
        </div>
        <div class="kpi-details">
          <span class="kpi-name">Tin tạo tháng này</span>
          <div class="kpi-number text-teal">{{ overview.monthCount }} <span class="kpi-unit">tin</span></div>
          <div class="kpi-hint">{{ monthLabel }}</div>
        </div>
      </div>

      <!-- Cấp trường đăng -->
      <div class="news-kpi-card border-amber">
        <div class="kpi-icon-wrap bg-amber-soft text-amber">
          <i class="el-icon-office-building"></i>
        </div>
        <div class="kpi-details">
          <span class="kpi-name">Trường đăng</span>
          <div class="kpi-number text-amber">{{ overview.schoolMonthCount }} <span class="kpi-unit">tin</span></div>
          <div class="kpi-hint">{{ overview.schoolMonthRate }}% tổng tin tháng</div>
        </div>
      </div>

      <!-- Cấp lớp đăng -->
      <div class="news-kpi-card border-purple">
        <div class="kpi-icon-wrap bg-purple-soft text-purple">
          <i class="el-icon-s-home"></i>
        </div>
        <div class="kpi-details">
          <span class="kpi-name">Lớp đăng</span>
          <div class="kpi-number text-purple">{{ overview.classMonthCount }} <span class="kpi-unit">tin</span></div>
          <div class="kpi-hint">{{ overview.classMonthRate }}% tổng tin tháng</div>
        </div>
      </div>
    </div>

    <!-- 2. Khu vực Biểu đồ trong tuần & Bảng 10 tin tức mới nhất -->
    <el-row :gutter="20" class="news-content-row">
      <!-- Biểu đồ tin tức trong tuần -->
      <el-col :xs="24" :sm="24" :md="10" :lg="9">
        <div class="card-box h-100">
          <div class="card-box-header">
            <div>
              <h3 class="box-title">Biểu Đồ Tin Tức Trong Tuần</h3>
              <p class="box-subtitle">Tin đang hiển thị theo ngày tạo và cấp đăng</p>
            </div>
            <span v-if="overview" class="tag-soft tag-soft--blue">Tuần này</span>
          </div>

          <div v-loading="loadingOverview" class="chart-wrapper" ref="chartWeeklyNews"></div>

          <div v-if="overview" class="chart-legend-row">
            <div class="legend-chip">
              <span class="chip-color chip-school"></span>
              <span>Trường đăng ({{ overview.weeklySchoolTotal }} tin)</span>
            </div>
            <div class="legend-chip">
              <span class="chip-color chip-class"></span>
              <span>Lớp đăng ({{ overview.weeklyClassTotal }} tin)</span>
            </div>
          </div>
        </div>
      </el-col>

      <!-- Bảng 10 tin tức mới nhất gọn gàng, có thể cuộn -->
      <el-col :xs="24" :sm="24" :md="14" :lg="15">
        <div class="card-box h-100">
          <div class="card-box-header">
            <div>
              <h3 class="box-title">Danh Sách Tin Tức Mới Nhất</h3>
              <p class="box-subtitle">10 tin được tạo gần nhất của trường</p>
            </div>

            <div class="header-filter-action">
              <el-radio-group v-model="filterScope" size="mini" class="compact-radio-group" @change="loadLatest">
                <el-radio-button label="ALL">Tất cả</el-radio-button>
                <el-radio-button label="SCHOOL">Cấp trường</el-radio-button>
                <el-radio-button label="CLASS">Cấp lớp</el-radio-button>
              </el-radio-group>
            </div>
          </div>

          <!-- Bảng cuộn gọn gàng -->
          <el-alert v-if="latestError" :title="latestError" type="error" show-icon :closable="false" class="news-api-alert" />
          <div v-loading="loadingLatest" class="table-scroll-container">
            <el-table
              :data="latestNewsList"
              size="small"
              stripe
              class="compact-news-table"
              empty-text="Không có tin tức"
              :header-cell-style="{ background: '#f1f5f9', color: '#334155', fontWeight: '700', fontSize: '12px' }"
              max-height="340"
            >
              <el-table-column type="index" label="STT" width="50" align="center" />

              <el-table-column label="Tiêu đề bài viết" min-width="230">
                <template slot-scope="scope">
                  <div class="news-title-cell">
                    <span class="news-title-text" :title="scope.row.title">
                      {{ scope.row.title }}
                    </span>
                  </div>
                </template>
              </el-table-column>

              <el-table-column label="Nguồn đăng" width="120" align="center">
                <template slot-scope="scope">
                  <span class="source-badge" :class="scope.row.scope === 'SCHOOL' ? 'badge-school' : scope.row.scope === 'CLASS' ? 'badge-class' : 'badge-unknown'">
                    <i :class="scope.row.scope === 'SCHOOL' ? 'el-icon-office-building' : 'el-icon-s-home'"></i>
                    {{ scope.row.sourceName }}
                  </span>
                </template>
              </el-table-column>

              <el-table-column label="Người tạo" width="130">
                <template slot-scope="scope">{{ scope.row.author || '—' }}</template>
              </el-table-column>

              <el-table-column label="Ngày tạo" width="125" align="center">
                <template slot-scope="scope">
                  <span class="time-text"><i class="el-icon-time"></i> {{ formatCreatedDate(scope.row.createdDate) }}</span>
                </template>
              </el-table-column>

              <el-table-column label="Trạng thái" width="105" align="center">
                <template slot-scope="scope">
                  <span class="status-tag" :class="scope.row.status === 'PUBLISHED' ? 'status-approved' : 'status-pending'">
                    {{ statusLabel(scope.row.status) }}
                  </span>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import * as echarts from "echarts";
import moment from "moment";
import NewsStatisticsService from "@/services/dashboard_v3/NewsStatisticsService";

const weekDays = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7", "Chủ Nhật"];
const statusNames = {
  PENDING_APPROVAL: "Chờ duyệt",
  SCHEDULED: "Đã lên lịch",
  SEND_FAILED: "Gửi lỗi",
  HIDDEN: "Đang ẩn",
  PUBLISHED: "Đã đăng",
  PROCESSING: "Đang xử lý"
};

export default {
  name: "NewsStatisticsV3",
  props: {
    autoLoad: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      hasLoadedData: false,
      isLoadingData: false,
      overview: null,
      filterScope: "ALL",
      latestNewsList: [],
      loadingOverview: false,
      loadingLatest: false,
      overviewError: "",
      latestError: "",
      overviewRequestId: 0,
      latestRequestId: 0,
      chartInstance: null
    };
  },
  computed: {
    deltaLabel() {
      if (!this.overview) return "";
      const delta = this.overview.yesterdayDelta;
      if (delta === 0) return "Bằng hôm qua";
      return (delta > 0 ? "+" : "") + delta + " tin so với hôm qua";
    },
    weekLabel() {
      if (!this.overview) return "";
      return moment(this.overview.weekStartDate).format("DD/MM") + " - " +
        moment(this.overview.weekEndDate).format("DD/MM/YYYY");
    },
    monthLabel() {
      return this.overview ? "Tháng " + moment(this.overview.monthStartDate).format("MM/YYYY") : "";
    }
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
    if (this.autoLoad) {
      this.loadData();
    }
  },
  beforeDestroy() {
    this.overviewRequestId += 1;
    this.latestRequestId += 1;
    window.removeEventListener("resize", this.handleResize);
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
  },
  methods: {
    async loadData() {
      if (this.hasLoadedData || this.isLoadingData) return;
      this.isLoadingData = true;
      try {
        await this.loadOverview();
        await this.loadLatest();
        this.hasLoadedData = true;
      } finally {
        this.isLoadingData = false;
      }
    },
    async loadOverview() {
      const requestId = ++this.overviewRequestId;
      this.loadingOverview = true;
      this.overviewError = "";
      this.overview = null;
      if (this.chartInstance) this.chartInstance.clear();
      try {
        const response = await NewsStatisticsService.getOverview();
        if (requestId !== this.overviewRequestId) return;
        const data = response && response.data && response.data.data;
        if (!data || !Array.isArray(data.weeklyPoints) || data.weeklyPoints.length !== 7) {
          throw new Error("Dữ liệu thống kê tin tức không hợp lệ");
        }
        this.overview = data;
        this.$nextTick(this.renderChart);
      } catch (error) {
        if (requestId === this.overviewRequestId) {
          this.overviewError = "Không tải được thống kê tin tức. Vui lòng thử lại.";
        }
      } finally {
        if (requestId === this.overviewRequestId) this.loadingOverview = false;
      }
    },
    async loadLatest() {
      const requestId = ++this.latestRequestId;
      const scope = this.filterScope;
      this.loadingLatest = true;
      this.latestError = "";
      this.latestNewsList = [];
      try {
        const response = await NewsStatisticsService.getLatest(scope);
        if (requestId !== this.latestRequestId) return;
        const data = response && response.data && response.data.data;
        if (!data || data.scope !== scope || !Array.isArray(data.rows)) {
          throw new Error("Danh sách tin tức không hợp lệ");
        }
        this.latestNewsList = data.rows;
      } catch (error) {
        if (requestId === this.latestRequestId) {
          this.latestError = "Không tải được danh sách tin tức. Vui lòng thử lại.";
        }
      } finally {
        if (requestId === this.latestRequestId) this.loadingLatest = false;
      }
    },
    formatCreatedDate(value) {
      return value ? moment(value).format("DD/MM HH:mm") : "—";
    },
    statusLabel(status) {
      return statusNames[status] || "Chưa xác định";
    },
    handleResize() {
      if (this.chartInstance) this.chartInstance.resize();
    },
    renderChart() {
      const dom = this.$refs.chartWeeklyNews;
      if (!dom || !this.overview) return;
      if (!this.chartInstance) this.chartInstance = echarts.init(dom);
      const points = this.overview.weeklyPoints;
      this.chartInstance.setOption({
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        grid: { top: 30, left: 10, right: 15, bottom: 10, containLabel: true },
        xAxis: {
          type: "category",
          data: weekDays,
          axisLine: { lineStyle: { color: "#cbd5e1" } },
          axisTick: { show: false },
          axisLabel: { color: "#475569", fontWeight: 600, fontSize: 11.5 }
        },
        yAxis: {
          type: "value",
          minInterval: 1,
          axisLine: { show: false },
          splitLine: { lineStyle: { color: "#f1f5f9", type: "dashed" } },
          axisLabel: { color: "#94a3b8" }
        },
        series: [
          {
            name: "Trường đăng",
            type: "bar",
            stack: "newsStack",
            barWidth: 22,
            data: points.map(point => point.schoolCount),
            itemStyle: { color: "#3b82f6" }
          },
          {
            name: "Lớp đăng",
            type: "bar",
            stack: "newsStack",
            barWidth: 22,
            data: points.map(point => point.classCount),
            itemStyle: { color: "#10b981", borderRadius: [6, 6, 0, 0] }
          }
        ]
      });
      this.chartInstance.resize();
    }
  }
};
</script>

<style scoped>
.news-stat-v3 {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 4px 20px -3px rgba(0, 0, 0, 0.04);
  margin-bottom: 28px;
}

/* Header */
.stat-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 22px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
}

.header-title-group {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: #fef3c7;
  color: #d97706;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.header-title {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.header-subtitle {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.live-indicator {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #d97706;
  background: #fffbeb;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid #fde68a;
}

.pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.25);
  animation: pulseDot 2s infinite;
}

@keyframes pulseDot {
  0% { transform: scale(0.95); opacity: 0.8; }
  50% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.8; }
}

/* KPI Strip Grid */
.news-kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;
  margin-bottom: 24px;
}

.news-kpi-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  transition: all 0.2s ease;
}

.news-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.04);
}

.kpi-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.kpi-details {
  flex: 1;
  min-width: 0;
}

.kpi-name {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 3px;
}

.kpi-number {
  font-size: 22px;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 4px;
}

.kpi-unit {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
}

.kpi-hint {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Border hints */
.border-indigo { border-left: 3.5px solid #4f46e5; }
.border-blue { border-left: 3.5px solid #0284c7; }
.border-teal { border-left: 3.5px solid #0d9488; }
.border-amber { border-left: 3.5px solid #d97706; }
.border-purple { border-left: 3.5px solid #8b5cf6; }

/* Colors */
.text-indigo { color: #4f46e5; }
.text-blue { color: #0284c7; }
.text-teal { color: #0d9488; }
.text-amber { color: #d97706; }
.text-purple { color: #8b5cf6; }
.text-green { color: #10b981; }

.bg-indigo-soft { background: #e0e7ff; }
.bg-blue-soft { background: #e0f2fe; }
.bg-teal-soft { background: #ccfbf1; }
.bg-amber-soft { background: #fef3c7; }
.bg-purple-soft { background: #f3e8ff; }

/* Content Row */
.news-content-row {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: -10px;
}

.news-content-row::before,
.news-content-row::after {
  display: none;
}

.news-content-row > .el-col {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.card-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  box-sizing: border-box;
}

.h-100 {
  height: 100%;
}

.card-box-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 14px;
}

.box-title {
  margin: 0 0 3px;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.box-subtitle {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
}

.tag-soft {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 8px;
}

.tag-soft--blue { background: #e0f2fe; color: #0369a1; }

.chart-wrapper {
  width: 100%;
  height: 250px;
  position: relative;
}

.chart-legend-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
}

.legend-chip {
  display: flex;
  align-items: center;
  gap: 6px;
}

.chip-color {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.chip-school { background: #3b82f6; }
.chip-class { background: #10b981; }

/* Table Section */
.compact-radio-group ::v-deep .el-radio-button__inner {
  font-size: 11.5px;
  padding: 6px 12px;
}

.table-scroll-container {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: #ffffff;
}

/* Custom table elements */
.news-title-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.news-title-text {
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.source-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 6px;
}

.badge-school {
  background: #e0f2fe;
  color: #0369a1;
}

.badge-class {
  background: #f3e8ff;
  color: #7e22ce;
}

.badge-unknown {
  background: #f1f5f9;
  color: #475569;
}

.news-api-alert {
  margin-bottom: 12px;
}

.time-text {
  font-size: 11.5px;
  color: #64748b;
  font-weight: 500;
}

.views-badge {
  font-size: 11.5px;
  font-weight: 700;
  color: #334155;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.status-tag {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
}

.status-approved {
  background: #dcfce7;
  color: #15803d;
}

.status-pending {
  background: #fef3c7;
  color: #b45309;
}

/* Responsive */
@media (max-width: 1300px) {
  .news-kpi-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .news-kpi-grid {
    grid-template-columns: 1fr;
  }
}
</style>
